import { useTranslation } from 'react-i18next'
import { LAYOUT_COLUMNS, RowLayout } from '../../schema/document'
import styles from './LayoutPicker.module.scss'

interface LayoutPickerProps {
  value?: RowLayout
  /** Layouts that cannot be chosen right now, with the reason shown as a title. */
  disabled?: RowLayout[]
  onPick: (layout: RowLayout) => void
}

const LAYOUTS = Object.keys(LAYOUT_COLUMNS) as RowLayout[]

/**
 * Choosing a row's shape by looking at it.
 *
 * §6 of the brief: a picture of the split, not a dropdown reading "1-2". The
 * person using this does not know what a column is called, but they can see
 * that one box is wider than the other.
 */
function LayoutPicker(props: LayoutPickerProps) {
  const { value, disabled = [], onPick } = props
  const { t } = useTranslation()

  return (
    <div className={styles.grid}>
      {LAYOUTS.map(layout => {
        const isDisabled = disabled.includes(layout)
        return (
          <button
            key={layout}
            type="button"
            className={`${styles.option} ${value === layout ? styles.optionActive : ''}`}
            disabled={isDisabled}
            title={isDisabled ? t('WEBSITE.LAYOUT.BLOCKED') : undefined}
            aria-label={t(`WEBSITE.LAYOUT.${layout}`)}
            aria-pressed={value === layout}
            onClick={() => onPick(layout)}
          >
            <span className={styles.preview}>
              {LAYOUT_COLUMNS[layout].map((fraction, index) => (
                <span
                  // The fractions of one layout are fixed and never reordered,
                  // so their position is the only identity they have.
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  className={styles.segment}
                  style={{ flexGrow: fraction }}
                />
              ))}
            </span>
            <span className={`label-bold-medium ${styles.caption}`}>
              {t(`WEBSITE.LAYOUT.${layout}`)}
            </span>
          </button>
        )
      })}
    </div>
  )
}

export default LayoutPicker
